import { useState } from 'react'
import { createUseStyles, useTheme } from 'react-jss'
import { DownOutlined, RightOutlined } from '@ant-design/icons'
import PropTypes from 'prop-types'

const useStyles = createUseStyles({
  card: {
    background: '#fff',
    border: '1px solid #f0f0f0',
    borderRadius: 8,
    padding: 24,
    marginBottom: 16,
  },
  cardCollapsed: {
    padding: '16px 24px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  headerCollapsed: {
    marginBottom: 0,
  },
  headerClickable: {
    cursor: 'pointer',
    userSelect: 'none',
    background: 'none',
    border: 'none',
    padding: 0,
    width: '100%',
    font: 'inherit',
    textAlign: 'left',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  icon: {
    fontSize: 18,
    color: ({ theme }) => theme.brandPrimary,
    display: 'flex',
    alignItems: 'center',
  },
  title: {
    fontSize: 16,
    fontWeight: 600,
    color: ({ theme }) => theme.textColor,
    margin: 0,
    lineHeight: '24px',
  },
  chevron: {
    fontSize: 12,
    color: '#999',
    transition: 'transform 0.2s',
    display: 'flex',
    alignItems: 'center',
  },
  body: {
    overflow: 'hidden',
    transition: 'max-height 0.25s ease, opacity 0.2s ease',
    maxHeight: 5000,
    opacity: 1,
  },
  bodyCollapsed: {
    maxHeight: 0,
    opacity: 0,
  },
})

export default function SectionCard({
  title = undefined,
  icon = undefined,
  extra = undefined,
  children = undefined,
  noPadding = false,
  collapsible = false,
  defaultCollapsed = false,
  onCollapseChange = undefined,
  style = {},
}) {
  const theme = useTheme()
  const classes = useStyles({ theme })
  const [collapsed, setCollapsed] = useState(defaultCollapsed)

  const isCollapsed = collapsible && collapsed

  return (
    <div
      className={`${classes.card}${isCollapsed ? ` ${classes.cardCollapsed}` : ''}`}
      style={{
        ...(noPadding ? { padding: 0 } : {}),
        ...style,
      }}
    >
      {title && !collapsible && (
        <div className={classes.header}>
          <div className={classes.titleRow}>
            {icon && <span className={classes.icon}>{icon}</span>}
            <h3 className={classes.title}>{title}</h3>
          </div>
          {extra && <div>{extra}</div>}
        </div>
      )}
      {title && collapsible && (
        <button
          type="button"
          className={`${classes.header}${isCollapsed ? ` ${classes.headerCollapsed}` : ''} ${classes.headerClickable}`}
          aria-expanded={!isCollapsed}
          onClick={() => setCollapsed(c => {
            const next = !c
            onCollapseChange?.(next)
            return next
          })}
        >
          <div className={classes.titleRow}>
            <span className={classes.chevron}>
              {isCollapsed ? <RightOutlined /> : <DownOutlined />}
            </span>
            {icon && <span className={classes.icon}>{icon}</span>}
            <h3 className={classes.title}>{title}</h3>
          </div>
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div onClick={e => e.stopPropagation()}>
            {extra && <div>{extra}</div>}
          </div>
        </button>
      )}
      {!title && children && children}
      {title && (
        <div className={`${classes.body}${isCollapsed ? ` ${classes.bodyCollapsed}` : ''}`}>
          {children}
        </div>
      )}
    </div>
  )
}

SectionCard.propTypes = {
  title: PropTypes.node,
  icon: PropTypes.node,
  extra: PropTypes.node,
  children: PropTypes.node,
  noPadding: PropTypes.bool,
  collapsible: PropTypes.bool,
  defaultCollapsed: PropTypes.bool,
  onCollapseChange: PropTypes.func,
  style: PropTypes.shape(),
}
